<template>
  <div class="content">
    <h2>Результаты единых государственных экзаменов (ЕГЭ)</h2>

    <div class="join join--col" v-if="stateExams.length">
      <fieldset
        class="form form--fieldset"
        v-for="exam in stateExams"
        :key="exam.id"
        disabled
      >
        <div class="form__group">
          <t-text :model-value="exam.discipline" label="Предмет"/>
          <t-text :model-value="exam.mark ?? '—'" label="Балл"/>
          <t-text :model-value="exam.date ?? '—'" label="Дата сдачи"/>
          <div class="form__status">
            <label class="label label--muted label--semibold">&nbsp;</label>
            <div class="badge badge--success badge--w-lg badge--mobile-full" v-if="exam.has_confirmation">Подтверждено</div>
            <div class="badge badge--primary badge--w-lg badge--mobile-full" v-else>В обработке</div>
          </div>
        </div>
      </fieldset>
    </div>
    <div v-else class="card card--info">
      <img class="card__icon icon icon--primary" src="~images/info.svg" v-svg-inline/>
      <p>Результаты ЕГЭ не добавлены</p>
    </div>

    <hr>

    <h2>Вступительные испытания, проводимые ТУСУР самостоятельно</h2>

    <div class="card card--info" v-if="!readonly">
      <img class="card__icon icon icon--primary" src="~images/info.svg" v-svg-inline/>
      <p>
        Для сдачи дистанционных вступительных испытаний и просмотра результатов перейдите по
        <router-link :to="{ name: 'abiturient.index', params: { tab: 'exams' } }">ссылке</router-link>
      </p>
    </div>

    <fieldset class="form__group" disabled v-if="store.application.exam_reason">
      <t-text-growing :model-value="store.application.exam_reason ?? '—'" label="Причина сдачи"/>
    </fieldset>

    <template v-if="entranceExams.length">
      <template v-for="exam in entranceExams" :key="exam.id">
        <hr>

        <div class="form__fieldset-group">
          <fieldset class="form form--fieldset" v-if="exam.is_editable && !readonly">
            <div class="form__group">
              <fieldset disabled>
                <t-text :model-value="exam.discipline_text" label="Предмет"/>
              </fieldset>
              <t-select
                v-model="selections[exam.id].classroom"
                :options="classroomOptionsFor(exam)"
                label="Место проведения"
                placeholder="Выберите место"
                @update:model-value="onClassroomChange(exam.id)"
              />
              <t-select
                v-model="selections[exam.id].scheduleId"
                :options="scheduleOptionsFor(exam)"
                label="Время проведения"
                placeholder="Выберите время"
                @update:model-value="clearEntranceExamErrors(exam.id)"
              />
              <div style="width: 100%">
                <label class="label label--muted label--semibold">&nbsp;</label>
                <button
                  type="button"
                  class="btn btn--primary btn--full-width"
                  :disabled="!selections[exam.id]?.scheduleId || selections[exam.id]?.scheduleId === exam.schedule_id"
                  @click="confirmExam = exam"
                >
                  Сохранить
                </button>
              </div>
            </div>
          </fieldset>

          <fieldset class="form form--fieldset" v-else disabled>
            <div class="form__group">
              <t-text :model-value="exam.discipline_text" label="Предмет"/>
              <t-text :model-value="exam.location?.message ?? '—'" label="Место проведения"/>
              <t-text :model-value="exam.exam_date ?? '—'" label="Время проведения"/>
            </div>
          </fieldset>

          <div class="form__errors" v-if="entranceExamErrorsFor(exam)?.length">
            <t-errors :errors="entranceExamErrorsFor(exam)" />
          </div>
        </div>

      </template>
    </template>
    <p v-else>Вступительные испытания не назначены</p>

    <t-modal v-if="confirmExam" @close="confirmExam = null">
      <div class="modal-header">Подтверждение записи</div>
      <div class="modal-body">
        <p>
          Записаться на вступительное испытание
          <strong>«{{ confirmExam.discipline_text }}»</strong>?
        </p>
        <p>
          Место проведения: <strong>{{ selections[confirmExam.id].classroom }}</strong><br>
          Время проведения: <strong>{{ confirmedScheduleText }}</strong>
        </p>
        <p>Изменить запись после подтверждения возможно только через обращение в приёмную комиссию.</p>
      </div>
      <div class="modal-footer join">
        <button class="btn btn--secondary btn--full-width" type="button" @click="confirmExam = null">
          Отмена
        </button>
        <button
          class="btn btn--primary btn--full-width"
          type="button"
          :disabled="isSaving"
          @click="onConfirmSave"
        >
          {{ isSaving ? 'Сохранение...' : 'Подтвердить' }}
        </button>
      </div>
    </t-modal>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { TText, TTextGrowing, TSelect, TModal, TErrors } from '@shared/ui'
import { useApplicationStore } from '@widgets/application-profile'

defineProps({
  readonly: { type: Boolean, default: false },
})

const store = useApplicationStore()

const stateExams = computed(() => store.application.state_exams ?? [])
const entranceExams = computed(() => store.application.entrance_exams ?? [])

const selections = ref({})
//создаем локальное состояние вида [{exam.id: { classroom: null, scheduleId: null }}, {}, {}]
watch(entranceExams, (exams) => {
  selections.value = Object.fromEntries(
    exams.map(exam => [exam.id, {
      classroom: exam.classroom ?? null,
      scheduleId: exam.schedule_id ?? null,
    }])
  )
}, { immediate: true })

//по disciplineId достаем места проведения из словаря
const findClassrooms = (disciplineId) =>
  store.entranceExamDisciplines?.find(discipline => discipline.discipline_id === disciplineId)?.classrooms ?? []

//по scheduleId находим ее в словаре перебором всех дисциплин и мест проведения
const findSchedule = (scheduleId) => {
  for (const discipline of store.entranceExamDisciplines ?? []) {
    for (const classroom of discipline.classrooms ?? []) {
      const schedule = classroom.schedules?.find(item => item.value === scheduleId)
      if (schedule) return schedule
    }
  }
  return null
}

// проверяем, есть ли среди notEditable экзаменов или среди тех которых выбрали но еще не отправили, повторяющиеся даты по полю exam_start/exam_date
const examStartFor = (exam) =>
  exam.is_editable
    ? findSchedule(selections.value[exam.id]?.scheduleId)?.exam_start
    : exam.exam_date

//массив exam_start/exam_date из cshedule у экзаменов
const occupiedStartsFor = (currentExam) =>
  entranceExams.value
    .filter(exam => exam.id !== currentExam.id)
    .map(examStartFor)
    .filter(Boolean)

//опции для места проведения
const classroomOptionsFor = (exam) =>
  findClassrooms(exam.discipline_id).map(classroom => ({
    value: classroom.value,
    text: classroom.text,
  }))

// опции для времени
const scheduleOptionsFor = (exam) => {
  const classrooms = findClassrooms(exam.discipline_id)
  const selectedClassroom = classrooms.find(classroom => classroom.value === selections.value[exam.id].classroom)
  const occupiedStarts = occupiedStartsFor(exam)
  return selectedClassroom?.schedules?.map(schedule => {
    const isTaken = occupiedStarts.includes(schedule.exam_start)
    return {
      value: schedule.value,
      text: schedule.text,
      disabled: isTaken,
      disabledReason: isTaken ? 'Это время уже занято другим испытанием' : null,
    }
  }) ?? []
}

//модалка с подтверждением по конкретному экзамену
const confirmExam = ref(null)
const isSaving = ref(false)
const confirmedScheduleText = computed(() =>
  findSchedule(selections.value[confirmExam.value?.id]?.scheduleId)?.text ?? ''
)

const entranceExamErrorsFor = (exam) => store.entranceExamErrors[exam.id]

const clearEntranceExamErrors = (examId) => {
  store.clearEntranceExamError(examId)
}

const onClassroomChange = (examId) => {
  selections.value[examId].scheduleId = null
  store.clearEntranceExamError(examId)
}

const onConfirmSave = async () => {
  if (isSaving.value) return
  isSaving.value = true
  const examId = confirmExam.value.id
  try {
    store.clearEntranceExamError(examId)
    await store.updateEntranceExam(
      examId,
      selections.value[examId].scheduleId,
    )
    confirmExam.value = null
  } finally {
    isSaving.value = false
  }
}
</script>
